import BlockIcon from "@mui/icons-material/Block"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ErrorIcon from "@mui/icons-material/Error"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import WarningIcon from "@mui/icons-material/Warning"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from "@mui/material"
import { t } from "i18next"
import React, { useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../hooks/useRedux"
import { clearImportResult, importFileAction } from "../../../store/slices/pencilImport.slice"
import { confirmOnEnter } from "../../../helpers/dialog.helpers"

interface PencilImportDialogProps {
    open: boolean
    onClose: () => void
}

export const PencilImportDialog: React.FC<PencilImportDialogProps> = ({ open, onClose }) => {
    const dispatch = useAppDispatch()
    const isImporting = useAppSelector((state) => state.pencilImport.isImporting)
    const lastImportResult = useAppSelector((state) => state.pencilImport.lastImportResult)
    const error = useAppSelector((state) => state.pencilImport.error)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)

    const hasResult = !!lastImportResult || !!error
    const phase: "select" | "importing" | "result" = isImporting
        ? "importing"
        : hasResult
        ? "result"
        : "select"

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            setSelectedFile(file)
        }
    }

    const handleImport = async () => {
        if (!selectedFile) return

        await dispatch(importFileAction(selectedFile))
    }

    const handleClose = () => {
        setSelectedFile(null)
        dispatch(clearImportResult())
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={phase === "importing" ? undefined : handleClose}
            onKeyDown={
                phase === "result"
                    ? confirmOnEnter(handleClose)
                    : confirmOnEnter(handleImport, phase === "importing" || !selectedFile)
            }
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>{t("editor:importDialog.title")}</DialogTitle>
            <DialogContent>
                {/* Phase: Select file */}
                {phase === "select" && (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {t("editor:importDialog.description")}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <input
                                accept=".pen,.tw,.json,.zip"
                                style={{ display: "none" }}
                                id="pencil-file-input"
                                type="file"
                                onChange={handleFileSelect}
                            />
                            <label htmlFor="pencil-file-input">
                                <Button variant="outlined" component="span" fullWidth>
                                    {selectedFile
                                        ? selectedFile.name
                                        : t("editor:importDialog.chooseFile")}
                                </Button>
                            </label>
                        </Box>
                    </>
                )}

                {/* Phase: Importing */}
                {phase === "importing" && (
                    <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Phase: Result */}
                {phase === "result" && (
                    <>
                        {/* Error Display */}
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Import Result */}
                        {lastImportResult && (
                            <Box>
                                <Alert
                                    severity={
                                        lastImportResult.errors.length > 0
                                            ? "error"
                                            : lastImportResult.warnings.length > 0
                                            ? "warning"
                                            : "success"
                                    }
                                    sx={{ mb: 2 }}
                                >
                                    <Typography variant="subtitle2" gutterBottom>
                                        {lastImportResult.errors.length > 0
                                            ? t("editor:importDialog.resultFailed")
                                            : t("editor:importDialog.resultCompleted")}
                                    </Typography>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    {t("editor:importDialog.stats.converted")}
                                                </TableCell>
                                                <TableCell>
                                                    {lastImportResult.stats.convertedNodes} /{" "}
                                                    {lastImportResult.stats.totalNodes}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    {t("editor:importDialog.stats.skipped")}
                                                </TableCell>
                                                <TableCell>
                                                    {lastImportResult.stats.skippedNodes}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    {t("editor:importDialog.stats.warnings")}
                                                </TableCell>
                                                <TableCell>
                                                    {lastImportResult.warnings.length}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    {t("editor:importDialog.stats.errors")}
                                                </TableCell>
                                                <TableCell>
                                                    {lastImportResult.errors.length}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Alert>

                                {/* Warnings */}
                                {lastImportResult.warnings.length > 0 && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <WarningIcon sx={{ mr: 1, color: "warning.main" }} />
                                            <Typography>
                                                {t("editor:importDialog.sections.warnings", {
                                                    total: lastImportResult.warnings.length,
                                                })}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box>
                                                {lastImportResult.warnings.map((warning, index) => (
                                                    <Box key={index} sx={{ mb: 1 }}>
                                                        <Typography variant="body2">
                                                            <strong>[{warning.type}]</strong>{" "}
                                                            {warning.message}
                                                        </Typography>
                                                        {warning.nodeName && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                {t("editor:importDialog.node", {
                                                                    name: warning.nodeName,
                                                                    id: warning.nodeId,
                                                                })}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Errors */}
                                {lastImportResult.errors.length > 0 && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <ErrorIcon sx={{ mr: 1, color: "error.main" }} />
                                            <Typography>
                                                {t("editor:importDialog.sections.errors", {
                                                    total: lastImportResult.errors.length,
                                                })}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box>
                                                {lastImportResult.errors.map((err, index) => (
                                                    <Box key={index} sx={{ mb: 1 }}>
                                                        <Typography variant="body2">
                                                            <strong>[{err.type}]</strong>{" "}
                                                            {err.message}
                                                        </Typography>
                                                        {err.nodeName && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                {t("editor:importDialog.node", {
                                                                    name: err.nodeName,
                                                                    id: err.nodeId,
                                                                })}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Skipped Objects */}
                                {lastImportResult.skippedNodes.length > 0 && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <BlockIcon sx={{ mr: 1, color: "text.secondary" }} />
                                            <Typography>
                                                {t("editor:importDialog.sections.skipped", {
                                                    total: lastImportResult.skippedNodes.length,
                                                })}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box>
                                                {lastImportResult.skippedNodes.map(
                                                    (node, index) => (
                                                        <Box key={index} sx={{ mb: 1 }}>
                                                            <Typography variant="body2">
                                                                <strong>[{node.nodeType}]</strong>{" "}
                                                                {node.nodeName || node.nodeId}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                {node.reason}
                                                            </Typography>
                                                        </Box>
                                                    )
                                                )}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Converted Objects */}
                                {lastImportResult.objects.length > 0 && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <CheckCircleIcon
                                                sx={{ mr: 1, color: "success.main" }}
                                            />
                                            <Typography>
                                                {t("editor:importDialog.sections.objects", {
                                                    total: lastImportResult.objects.length,
                                                })}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box>
                                                {lastImportResult.objects
                                                    .slice(0, 10)
                                                    .map((obj, index) => (
                                                        <Typography key={index} variant="body2">
                                                            {index + 1}. [{obj.type}] {obj.title}
                                                        </Typography>
                                                    ))}
                                                {lastImportResult.objects.length > 10 && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {t("editor:importDialog.andMore", {
                                                            total:
                                                                lastImportResult.objects.length -
                                                                10,
                                                        })}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                {phase === "select" && (
                    <>
                        <Button onClick={handleClose}>{t("common:disagree")}</Button>
                        <Button onClick={handleImport} variant="contained" disabled={!selectedFile}>
                            {t("editor:importDialog.buttonOpen")}
                        </Button>
                    </>
                )}
                {phase === "result" && (
                    <Button onClick={handleClose} variant="contained">
                        {t("common:agree")}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}
